import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import type { RewardImage } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import { RewardUnlockedAnimation } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useCardSetBranding } from '@sorare/core/src/set/branding/useCardSetBranding.ts';

import { ButtonsWrapper, Root } from './styles.tsx';

type Props = {
  header?: ReactNode;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  amount?: ReactNode;
  images: RewardImage[];
  rgbColor?: string;
  children?: ReactNode;
  action?: ReactNode;
  showContinueButton?: boolean;
  hideBackground?: boolean;
  useSetBackground?: boolean;
  disclaimer?: ReactNode;
};
export const DumbRewardUnlockedStory = ({
  header,
  onClose,
  title = <FormattedMessage defaultMessage="You've won a reward!" />,
  subtitle,
  amount,
  action,
  images,
  rgbColor = 'var(--c-rgb-green-400)',
  children,
  showContinueButton = true,
  hideBackground,
  useSetBackground,
  disclaimer,
}: Props) => {
  const isSeriesNav = useSeriesNav();
  const { rewardSuccessPopupBackgroundUrl } = useCardSetBranding();
  const shouldUseSetBackground =
    useSetBackground || (isSeriesNav && !!rewardSuccessPopupBackgroundUrl);
  const shouldHideBackground = hideBackground || shouldUseSetBackground;

  const continueButton = showContinueButton ? (
    <Button
      size="large"
      onClick={() => {
        onClose?.();
      }}
      color="primary"
      disableDebounce
    >
      <FormattedMessage {...glossary.continue} />
    </Button>
  ) : null;

  return (
    <Root
      style={
        {
          '--rgb-color': rgbColor,
          ...(shouldUseSetBackground && rewardSuccessPopupBackgroundUrl
            ? {
                background: `url(${rewardSuccessPopupBackgroundUrl}) center / cover no-repeat`,
              }
            : {}),
        } as unknown as CSSProperties
      }
      className={classNames({ hideBackground: shouldHideBackground })}
    >
      {header}
      <RewardUnlockedAnimation
        images={images}
        title={title}
        subtitle={subtitle}
        amount={amount}
      >
        {children}
      </RewardUnlockedAnimation>
      <ButtonsWrapper>
        {disclaimer}
        {continueButton}
        {action}
      </ButtonsWrapper>
    </Root>
  );
};
